import { usePress } from "@react-aria/interactions";
import { useFocusable } from "@react-aria/focus";
import { filterDOMProps, mergeProps, chain } from "@react-aria/utils";
import _babelRuntimeHelpersEsmExtends from "@babel/runtime/helpers/esm/extends";
/**
 * Provides the behavior and accessibility implementation for a button component. Handles mouse, keyboard, and touch interactions,
 * focus behavior, and ARIA props for both native button elements and custom element types.
 * @param props - Props to be applied to the button.
 * @param ref - A ref to a DOM element for the button.
 */

export function useButton(props, ref) {
  /* eslint-enable no-redeclare */
  var _props$elementType = props.elementType,
      elementType = _props$elementType === void 0 ? 'button' : _props$elementType,
      isDisabled = props.isDisabled,
      onPress = props.onPress,
      onPressStart = props.onPressStart,
      onPressEnd = props.onPressEnd,
      onPressChange = props.onPressChange,
      deprecatedOnClick = props.onClick,
      href = props.href,
      target = props.target,
      rel = props.rel,
      _props$type = props.type,
      type = _props$type === void 0 ? 'button' : _props$type;
  var additionalProps;

  if (elementType !== 'button') {
    additionalProps = {
      role: 'button',
      tabIndex: isDisabled ? undefined : 0,
      href: elementType === 'a' && isDisabled ? undefined : href,
      target: elementType === 'a' ? target : undefined,
      type: elementType === 'input' ? type : undefined,
      disabled: elementType === 'input' ? isDisabled : undefined,
      'aria-disabled': !isDisabled || elementType === 'input' ? undefined : isDisabled,
      rel: elementType === 'a' ? rel : undefined
    };
  }

  var _usePress = usePress({
    onPressStart: onPressStart,
    onPressEnd: onPressEnd,
    onPressChange: onPressChange,
    onPress: onPress,
    isDisabled: isDisabled,
    ref: ref
  }),
      pressProps = _usePress.pressProps,
      isPressed = _usePress.isPressed;

  var _useFocusable = useFocusable(props, ref),
      focusableProps = _useFocusable.focusableProps;

  var buttonProps = mergeProps(focusableProps, pressProps);
  buttonProps = mergeProps(buttonProps, filterDOMProps(props, {
    labelable: true
  }));
  return {
    isPressed: isPressed,
    // Used to indicate press state for visual
    buttonProps: mergeProps(buttonProps, _babelRuntimeHelpersEsmExtends({
      'aria-haspopup': props['aria-haspopup'],
      'aria-expanded': props['aria-expanded'],
      'aria-controls': props['aria-controls'],
      'aria-pressed': props['aria-pressed'],
      disabled: isDisabled,
      type: type
    }, additionalProps || {}, {
      onClick: function onClick(e) {
        if (deprecatedOnClick) {
          deprecatedOnClick(e);
          console.warn('onClick is deprecated, please use onPress');
        }
      }
    }))
  };
}
/**
 * Provides the behavior and accessibility implementation for a toggle button component.
 * ToggleButtons allow users to toggle a selection on or off, for example switching between two states or modes.
 */

export function useToggleButton(props, state, ref) {
  /* eslint-enable no-redeclare */
  var isSelected = state.isSelected;

  var _useButton = useButton(_babelRuntimeHelpersEsmExtends({}, props, {
    onPress: chain(state.toggle, props.onPress)
  }), ref),
      isPressed = _useButton.isPressed,
      buttonProps = _useButton.buttonProps;

  return {
    isPressed: isPressed,
    buttonProps: mergeProps(buttonProps, {
      'aria-pressed': isSelected
    })
  };
}